import React from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import { AppProvider, useApp } from "./lib/AppContext";
import ErrorBoundary from "./components/ErrorBoundary";
import Login from "./pages/Login";
import JobSelect from "./pages/JobSelect";
import Dashboard from "./pages/Dashboard";
import TimeAllocation from "./pages/TimeAllocation";
import Camera from "./pages/Camera";
import QuickPhoto from "./pages/QuickPhoto";
import Status from "./pages/Status";
import MyTimesheets from "./pages/MyTimesheets";
import DriverMode from "./pages/DriverMode";
import ReceiptScanner from "./pages/ReceiptScanner";

function AppContent() {
  const {
    orgId,
    organization,
    currentUser,
    location,
    jobs,
    jobsLoading,
    selectedJob,
    crew,
    costCodes,
    vocabulary,
    currentView,
    menuOpen,
    navigate,
    selectJob,
    setMenuOpen,
    setSelectedJob,
    clockInMember,
    clockOutMember,
    bulkClockIn,
    bulkClockOut,
    updateTime,
    saveAllocations,
    loadCrew,
    signOut,
    pendingVoiceAllocation,
    getPendingVoice,
    clearPendingVoiceAllocation,
  } = useApp();

  // Check if current user is a driver (or admin/office for testing)
  const isDriver = currentUser?.employee_type === "driver";
  const canAccessDriverMode =
    isDriver ||
    currentUser?.employee_type === "admin" ||
    currentUser?.employee_type === "office";

  // Slide-out Menu
  const SlideMenu = () => (
    <>
      {menuOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 50 }}>
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
            }}
            onClick={() => setMenuOpen(false)}
          />
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "280px",
              background: "white",
              boxShadow: "4px 0 24px rgba(0,0,0,0.15)",
            }}
          >
            <div
              style={{
                background: "#1a5a8e",
                color: "white",
                padding: "48px 24px 24px",
              }}
            >
              <div
                style={{
                  width: "64px",
                  height: "64px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px",
                  fontWeight: "600",
                  marginBottom: "12px",
                }}
              >
                {currentUser?.initials || "??"}
              </div>
              <div style={{ fontWeight: "600", fontSize: "18px" }}>
                {currentUser?.name || "Guest"}
              </div>
              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "14px" }}>
                {currentUser?.role
                  ? currentUser.role.charAt(0).toUpperCase() +
                    currentUser.role.slice(1)
                  : "User"}
              </div>
            </div>
            <nav style={{ padding: "16px" }}>
              {[
                {
                  icon: "🚛",
                  label: "Driver Mode",
                  view: "driver",
                  showIf: canAccessDriverMode,
                },
                { icon: "📍", label: "Select Job", view: "jobs" },
                { icon: "⏱", label: "Allocate", view: "time" },
                { icon: "📷", label: "Photos", view: "camera" },
                { icon: "🧾", label: "Snap Receipt", view: "receipt" },
                { icon: "📊", label: "Production", view: "status" },
                { icon: "📋", label: "My Timesheets", view: "timesheets" },
              ]
                .filter((item) => item.showIf !== false)
                .map((item) => (
                  <button
                    key={item.view}
                    onClick={() => {
                      if (item.view === "jobs") {
                        setSelectedJob(null);
                      }
                      navigate(item.view);
                    }}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                      padding: "12px 16px",
                      borderRadius: "12px",
                      textAlign: "left",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "15px",
                      fontWeight: "500",
                      color: "#334155",
                    }}
                  >
                    <span style={{ fontSize: "20px" }}>{item.icon}</span>
                    <span>{item.label}</span>
                  </button>
                ))}

              {/* Logout Button */}
              <div
                style={{
                  borderTop: "1px solid #e2e8f0",
                  marginTop: "16px",
                  paddingTop: "16px",
                }}
              >
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    signOut();
                  }}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    padding: "12px 16px",
                    borderRadius: "12px",
                    textAlign: "left",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "15px",
                    fontWeight: "500",
                    color: "#dc2626",
                  }}
                >
                  <span style={{ fontSize: "20px" }}>🚪</span>
                  <span>Logout</span>
                </button>
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );

  // Transform jobs for JobSelect component
  const transformedJobs = jobs.map((job) => ({
    id: job.job_number || job.id,
    name: job.name,
    distance: job.distanceText || "-- mi",
    closest: job.isClosest || false,
    status: job.status,
    day: 1, // Would come from calculated days since start
    totalDays: job.estimated_days || 10,
    type: job.cost_code_template || "brick",
    // Keep original data for selection
    _original: job,
  }));

  // Show loading state while determining initial route
  if (!currentView) {
    return (
      <div
        style={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          maxWidth: "430px",
          margin: "0 auto",
          background: "#f8fafc",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 0 40px rgba(0,0,0,0.1)",
        }}
      >
        <div style={{ textAlign: "center", color: "#64748b" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "3px solid #e2e8f0",
              borderTopColor: "#1a5a8e",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 12px",
            }}
          />
          <p style={{ margin: 0, fontSize: "14px" }}>Loading...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        maxWidth: "430px",
        margin: "0 auto",
        background: "#f8fafc",
        minHeight: "100vh",
        position: "relative",
        boxShadow: "0 0 40px rgba(0,0,0,0.1)",
      }}
    >
      <SlideMenu />

      {currentView === "jobs" && (
        <ErrorBoundary>
          <JobSelect
            jobs={transformedJobs}
            loading={jobsLoading}
            currentUser={currentUser}
            orgId={orgId}
            onSelectJob={(job) => selectJob(job._original || job)}
            onMenuOpen={() => setMenuOpen(true)}
          />
        </ErrorBoundary>
      )}

      {currentView === "command" && selectedJob && (
        <ErrorBoundary>
          <Dashboard
            job={{
              id: selectedJob.job_number || selectedJob.id,
              rawId: selectedJob.id,
              name: selectedJob.name,
              status: selectedJob.status,
              day: 1,
              totalDays: selectedJob.estimated_days || 10,
            }}
            currentUser={currentUser}
            crew={crew.map((m) => ({
              id: m.id,
              name: m.display_name || `${m.last_name}, ${m.first_name}`,
              type: m.role,
              color: m.color || m.avatar_color || "#64748b",
              clockIn: m.clockIn,
              clockOut: m.clockOut,
              timeEntry: m.timeEntry,
              totalClocked: m.totalHours || 0,
              allocations: m.allocations || [],
            }))}
            orgId={orgId}
            vocabulary={vocabulary}
            onCrewAdded={loadCrew}
            onClockIn={clockInMember}
            onClockOut={clockOutMember}
            onBulkClockIn={bulkClockIn}
            onBulkClockOut={bulkClockOut}
            onUpdateTime={updateTime}
            onNavigate={navigate}
            onChangeJob={() => {
              setSelectedJob(null);
              navigate("jobs");
            }}
            onMenuOpen={() => setMenuOpen(true)}
          />
        </ErrorBoundary>
      )}

      {currentView === "time" &&
        selectedJob &&
        (() => {
          // Use getPendingVoice() as fallback - ref is always current
          const voiceData = pendingVoiceAllocation || getPendingVoice();
          return (
            <ErrorBoundary>
              <TimeAllocation
                job={{
                  id: selectedJob.job_number || selectedJob.id,
                  rawId: selectedJob.id,
                  name: selectedJob.name,
                }}
                orgId={orgId}
                crew={crew.map((m) => ({
                  id: m.id,
                  name: m.display_name || `${m.last_name}, ${m.first_name}`,
                  type: m.role,
                  color: m.color || m.avatar_color || "#64748b",
                  clockIn: m.clockIn,
                  clockOut: m.clockOut,
                  totalClocked: m.totalHours || 0,
                  allocations: m.allocations || [],
                  timeEntry: m.timeEntry,
                }))}
                costCodes={costCodes}
                vocabulary={vocabulary}
                pendingVoiceAllocation={voiceData}
                onClearPendingVoice={clearPendingVoiceAllocation}
                onSaveAllocations={saveAllocations}
                onBack={() => navigate("command")}
              />
            </ErrorBoundary>
          );
        })()}

      {currentView === "camera" && selectedJob && (
        <ErrorBoundary>
          <Camera
            job={{
              id: selectedJob.id,
              job_number: selectedJob.job_number,
              name: selectedJob.name,
            }}
            currentUser={currentUser}
            orgId={orgId}
            location={location}
            onBack={() => navigate("command")}
          />
        </ErrorBoundary>
      )}

      {/* Quick Photo - when no job selected (drive-by photos) */}
      {currentView === "camera" && !selectedJob && (
        <ErrorBoundary>
          <QuickPhoto
            currentUser={currentUser}
            orgId={orgId}
            onBack={() => navigate("command")}
          />
        </ErrorBoundary>
      )}

      {currentView === "status" && selectedJob && (
        <ErrorBoundary>
          <Status
            job={{
              id: selectedJob.id,
              job_number: selectedJob.job_number,
              name: selectedJob.name,
            }}
            costCodes={costCodes}
            currentUser={currentUser}
            orgId={orgId}
            onBack={() => navigate("command")}
          />
        </ErrorBoundary>
      )}

      {currentView === "receipt" && (
        <ErrorBoundary>
          <ReceiptScanner
            preselectedJobId={selectedJob?.id}
            onBack={() => navigate(selectedJob ? "command" : "jobs")}
          />
        </ErrorBoundary>
      )}

      {currentView === "timesheets" && (
        <ErrorBoundary>
          <MyTimesheets
            currentUser={currentUser}
            orgId={orgId}
            onBack={() => navigate(selectedJob ? "command" : "jobs")}
          />
        </ErrorBoundary>
      )}

      {currentView === "driver" && (
        <ErrorBoundary>
          <DriverMode
            currentUser={currentUser}
            orgId={orgId}
            organization={organization}
            onMenuOpen={() => setMenuOpen(true)}
          />
        </ErrorBoundary>
      )}
    </div>
  );
}

function AuthenticatedApp() {
  const { loading, isAuthenticated, error } = useAuth();

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #1a5a8e 0%, #0f3a5e 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center", color: "white" }}>
          <div
            style={{
              width: "48px",
              height: "48px",
              border: "4px solid rgba(255,255,255,0.3)",
              borderTopColor: "white",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px",
            }}
          />
          <p>Loading...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Show error if employee record not found
  if (error) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #1a5a8e 0%, #0f3a5e 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "16px",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "24px",
            maxWidth: "400px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              background: "#fef2f2",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <span style={{ fontSize: "24px" }}>!</span>
          </div>
          <h2 style={{ margin: "0 0 8px", color: "#1e293b" }}>Access Error</h2>
          <p style={{ color: "#64748b", margin: "0 0 16px" }}>{error}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: "none",
              border: "none",
              color: "#1a5a8e",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Login />;
  }

  // User is authenticated - show main app
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AuthenticatedApp />
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </AuthProvider>
  );
}
